import { useRef, useState } from "react"
import { CaretDown, X } from "@phosphor-icons/react"
import { t } from "i18next"
import {
  DropdownMenu,
  DropdownMenuCheckboxItem,
  DropdownMenuContent,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu"
import { FormError } from "@/components/ui/form.tsx"
import { cn } from "@/lib/utils"
import type { PointOfCareOption } from "@/modules/patients/detail/progress-note/point-of-care/types"
import { moveFocusToNextElement } from "@/modules/patients/libs/move-focus-to-next-element"

interface ProgressNoteSelectProps<TValue extends string> {
  label: string
  options: ReadonlyArray<PointOfCareOption<TValue>>
  onSelect: (value: TValue | undefined) => void
  defaultValue: TValue | undefined | null
  testId: string
  isClearable?: boolean
  disabled?: boolean
  error?: string
}

export const ProgressNoteSelect = <TValue extends string>(props: ProgressNoteSelectProps<TValue>) => {
  const { label, onSelect, options, defaultValue, testId, isClearable = false, disabled = false, error } = props
  const [open, setOpen] = useState(false)
  const triggerRef = useRef(null)

  const activeLabel = options.find((option) => option.value === defaultValue)?.label

  return (
    <DropdownMenu open={open} onOpenChange={setOpen} modal={false}>
      <div>
        <DropdownMenuTrigger asChild data-testid={testId} disabled={disabled}>
          <button
            ref={triggerRef}
            className={cn(
              "custom-dropdown-trigger bg-surface-subdued relative flex h-12 w-full items-center justify-between gap-2 rounded-md border p-1 pl-4 text-sm",
              error && "outline-critical! outline-1 outline-solid",
            )}>
            <div>
              <label
                className={cn("absolute top-3.5 left-4 transition-all", defaultValue ? "top-1 text-xs" : "top-3.5")}>
                {label}
              </label>
              {defaultValue && <span className="absolute top-5.25 w-full truncate pr-12 text-left">{activeLabel}</span>}
            </div>
            <div
              className={cn(
                "text-on-surface-subdued z-10 flex h-10 w-10 items-center justify-center rounded-3xl",
                !disabled && (isClearable || (!isClearable && !defaultValue))
                  ? "hover:bg-surface-hovered focus-visible:bg-surface-focused active:bg-surface-pressed"
                  : "",
              )}
              onClick={() => (defaultValue && isClearable ? onSelect(undefined) : undefined)}>
              {isClearable && defaultValue ? (
                <X size={24} className="text-on-surface-subdued" />
              ) : (
                <CaretDown
                  className={cn("h-6 w-6", disabled ? "text-on-surface-disabled" : "text-on-surface-subdued")}
                />
              )}
            </div>
          </button>
        </DropdownMenuTrigger>

        {error && <FormError>{t("common.fieldErrorList.requiredField")}</FormError>}
      </div>
      <DropdownMenuContent
        side="bottom"
        align="start"
        className="grid h-(--radix-select-trigger-height) w-(--radix-dropdown-menu-trigger-width) gap-1 rounded-xl p-2"
        onKeyDown={(event) => {
          if (event.code === "Tab" && !event.shiftKey) {
            setOpen(false)
            moveFocusToNextElement(triggerRef.current)
          }
        }}>
        {options.map(({ value, label }) => (
          <DropdownMenuCheckboxItem
            className="data-[state='checked']:bg-primary-surface! data-disabled:text-on-surface-disabled data-[state='checked']:text-on-primary-surface! focus:bg-surface-hovered focus:text-on-surface w-full transition-all duration-100 data-disabled:pointer-events-none"
            onSelect={() => onSelect(value)}
            key={value}
            checked={value === defaultValue}
            data-testid={`point-of-care-option-${value}`}>
            {label}
          </DropdownMenuCheckboxItem>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  )
}
